import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, NodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { IframeExtension } from './extensions/IframeExtension';
import { KeyTakeawaysNode } from './extensions/KeyTakeawaysNode';
import { 
  Bold,
  Italic, 
  Strikethrough, 
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  Plus,
  Undo,
  Redo,
  ListChecks,
  Image as ImageIcon,
  Pencil,
  Trash2,
  ExternalLink,
  Code2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ArticleMediaLibrary } from './ArticleMediaLibrary';
import { InsertPollQuizModal } from './InsertPollQuizModal';
import { cn } from '@/lib/utils';
import { useArticleTypography, generateTypographyCSS } from '@/hooks/useArticleTypography';
import { useBrandManager } from '@/hooks/useBrandManager';

interface MediumStyleEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  brandId?: string;
  onLinkAdded?: (url: string, text: string) => void;
}

const MediumStyleEditor: React.FC<MediumStyleEditorProps> = ({
  value,
  onChange,
  placeholder = 'Tell your story...',
  brandId,
  onLinkAdded
}) => {
  const { currentBrandId } = useBrandManager();
  const effectiveBrandId = brandId || currentBrandId;
  const { typography } = useArticleTypography(effectiveBrandId);
  
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [floatingMenuOpen, setFloatingMenuOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [floatingButtonPos, setFloatingButtonPos] = useState({ top: 0 });
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [imageEditDialog, setImageEditDialog] = useState<{
    open: boolean;
    src: string;
    alt: string;
    title: string;
  }>({ open: false, src: '', alt: '', title: '' });
  const [linkDialog, setLinkDialog] = useState<{
    open: boolean;
    url: string;
    openInNewTab: boolean;
  }>({ open: false, url: '', openInNewTab: false });
  const [htmlDialog, setHtmlDialog] = useState<{
    open: boolean;
    html: string;
  }>({ open: false, html: '' });
  const [pollQuizModalOpen, setPollQuizModalOpen] = useState(false);

  // Generate dynamic typography CSS
  const dynamicTypographyCSS = useMemo(() => {
    return generateTypographyCSS(typography, '.medium-editor .ProseMirror');
  }, [typography]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-4 hover:text-primary/80',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto my-4 cursor-pointer',
        },
      }),
      IframeExtension,
      KeyTakeawaysNode,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      CharacterCount,
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'prose prose-lg dark:prose-invert max-w-none focus:outline-none min-h-[400px] px-1',
      },
      handleClick: (view, pos, event) => {
        const node = view.state.doc.nodeAt(pos);
        if (node?.type.name === 'image') {
          const attrs = node.attrs;
          setImageEditDialog({
            open: true,
            src: attrs.src || '',
            alt: attrs.alt || '',
            title: attrs.title || '',
          });
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      // Check if cursor is on an empty paragraph
      const { $from } = editor.state.selection;
      const currentNode = $from.parent;
      const isEmptyParagraph = currentNode.type.name === 'paragraph' && currentNode.content.size === 0;
      
      if (isEmptyParagraph && editorContainerRef.current) {
        // Get cursor position relative to editor container
        const coords = editor.view.coordsAtPos($from.pos);
        const containerRect = editorContainerRef.current.getBoundingClientRect();
        const top = coords.top - containerRect.top;
        
        setFloatingButtonPos({ top });
        setShowFloatingButton(true);
      } else {
        setShowFloatingButton(false);
        setFloatingMenuOpen(false);
      }
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      const currentContent = editor.getHTML();
      // Only update if the content is actually different
      if (value !== currentContent) {
        console.log('Updating editor content:', { 
          valueLength: value.length, 
          currentLength: currentContent.length,
          valuePreview: value.substring(0, 100),
          currentPreview: currentContent.substring(0, 100)
        });
        editor.commands.setContent(value || '');
      }
    }
  }, [editor, value]);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    
    const attrs = editor.getAttributes('link');
    setLinkDialog({
      open: true,
      url: attrs.href || '',
      openInNewTab: attrs.target === '_blank',
    });
  }, [editor]);

  const handleSaveLink = useCallback(() => {
    if (!editor) return;
    
    if (linkDialog.url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      // Get the selected text to use as the link title
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      
      editor.chain().focus().extendMarkRange('link').setLink({ 
        href: linkDialog.url,
        target: linkDialog.openInNewTab ? '_blank' : null,
        rel: linkDialog.openInNewTab ? 'noopener noreferrer' : null,
      }).run();
      
      // Notify parent about the new link for sources
      if (onLinkAdded && linkDialog.url) {
        onLinkAdded(linkDialog.url, selectedText || linkDialog.url);
      }
    }
    setLinkDialog({ open: false, url: '', openInNewTab: false });
  }, [editor, linkDialog, onLinkAdded]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkDialog({ open: false, url: '', openInNewTab: false });
  }, [editor]);

  const handleInsertImage = useCallback((url: string, alt?: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: url, alt: alt || '' }).run();
    // Also close edit dialog if replacing
    setImageEditDialog({ open: false, src: '', alt: '', title: '' });
  }, [editor]);

  const handleUpdateImage = useCallback(() => {
    if (!editor) return;
    
    editor.chain().focus().setImage({ 
      src: imageEditDialog.src, 
      alt: imageEditDialog.alt,
      title: imageEditDialog.title,
    }).run();
    
    setImageEditDialog({ open: false, src: '', alt: '', title: '' });
  }, [editor, imageEditDialog]);

  const handleDeleteImage = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().deleteSelection().run();
    setImageEditDialog({ open: false, src: '', alt: '', title: '' });
  }, [editor]);

  const handleInsertHtml = useCallback(() => {
    if (!editor || !htmlDialog.html.trim()) return;
    
    // Insert HTML content with proper parsing
    editor.chain().focus().insertContent(htmlDialog.html, {
      parseOptions: {
        preserveWhitespace: false,
      },
    }).run();
    setHtmlDialog({ open: false, html: '' });
  }, [editor, htmlDialog.html]);

  const handleInsertPollQuiz = useCallback((iframeHtml: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(iframeHtml, {
      parseOptions: {
        preserveWhitespace: false,
      },
    }).run();
  }, [editor]);

  if (!editor) return null;

  const handleFormatAction = (action: () => void) => {
    action();
    setFloatingMenuOpen(false);
    setShowFloatingButton(false);
  };

  return (
    <div className="medium-editor relative" ref={editorContainerRef}>
      {/* Bubble Menu - appears on text selection */}
      <BubbleMenu 
        editor={editor} 
        className="flex items-center gap-1 p-1 rounded-lg border bg-background shadow-lg"
      >
        <Toggle
          size="sm"
          pressed={editor.isActive('bold')}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('italic')}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('strike')}
          onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('code')}
          onPressedChange={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="h-4 w-4" />
        </Toggle>
        
        <Separator orientation="vertical" className="h-6 mx-1" />
        
        <Toggle
          size="sm"
          pressed={editor.isActive('link')}
          onPressedChange={openLinkDialog}
        >
          <LinkIcon className="h-4 w-4" />
        </Toggle>
        
        <Separator orientation="vertical" className="h-6 mx-1" />
        
        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 1 })}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 2 })}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 3 })}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="h-4 w-4" />
        </Toggle>
      </BubbleMenu>

      {/* Custom Floating Menu - appears on empty lines */}
      {showFloatingButton && (
        <div 
          className="absolute left-0 z-50"
          style={{ top: floatingButtonPos.top - 4, transform: 'translateX(-100%)' }}
        >
          <DropdownMenu open={floatingMenuOpen} onOpenChange={setFloatingMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full border-dashed mr-2"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              side="right" 
              align="start" 
              className="z-[1400]"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => setMediaLibraryOpen(true))}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Image
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleHeading({ level: 1 }).run())}
              >
                <Heading1 className="h-4 w-4 mr-2" />
                Heading 1
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleHeading({ level: 2 }).run())}
              >
                <Heading2 className="h-4 w-4 mr-2" />
                Heading 2
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleHeading({ level: 3 }).run())}
              >
                <Heading3 className="h-4 w-4 mr-2" />
                Heading 3
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleBulletList().run())}
              >
                <List className="h-4 w-4 mr-2" />
                Bullet List
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleOrderedList().run())}
              >
                <ListOrdered className="h-4 w-4 mr-2" />
                Numbered List
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleBlockquote().run())}
              >
                <Quote className="h-4 w-4 mr-2" />
                Quote
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().toggleCodeBlock().run())}
              >
                <Code className="h-4 w-4 mr-2" />
                Code Block
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => editor.chain().focus().setHorizontalRule().run())}
              >
                <Minus className="h-4 w-4 mr-2" />
                Divider
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => setHtmlDialog({ open: true, html: '' }))}
              >
                <Code2 className="h-4 w-4 mr-2" />
                HTML / Embed
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleFormatAction(() => setPollQuizModalOpen(true))}
              >
                <ListChecks className="h-4 w-4 mr-2" />
                Poll / Quiz
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Bottom Toolbar */}
      <div className="flex items-center justify-between pt-4 border-t mt-4">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-6 mx-2" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMediaLibraryOpen(true)}
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            Add Image
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">
          {editor.storage.characterCount?.words() || 0} words
        </div>
      </div>

      {/* Media Library Dialog */}
      <ArticleMediaLibrary
        open={mediaLibraryOpen}
        onOpenChange={setMediaLibraryOpen}
        onSelect={handleInsertImage}
      />

      {/* Poll/Quiz Insert Modal */}
      <InsertPollQuizModal
        open={pollQuizModalOpen}
        onOpenChange={setPollQuizModalOpen}
        onInsert={handleInsertPollQuiz}
      />

      {/* Image Edit Dialog */}
      <Dialog open={imageEditDialog.open} onOpenChange={(open) => !open && setImageEditDialog({ open: false, src: '', alt: '', title: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" />
              Edit Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {imageEditDialog.src && (
              <div className="rounded-lg overflow-hidden border bg-muted">
                <img 
                  src={imageEditDialog.src} 
                  alt={imageEditDialog.alt} 
                  className="w-full h-40 object-contain"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={imageEditDialog.src}
                onChange={(e) => setImageEditDialog(prev => ({ ...prev, src: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={imageEditDialog.title}
                onChange={(e) => setImageEditDialog(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Image title (shows on hover)"
              />
            </div>
            <div className="space-y-2">
              <Label>Alt Text</Label>
              <Input
                value={imageEditDialog.alt}
                disabled
                className="bg-muted cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                Alt text is set in Media Library. Use "Replace from Media Library" to change it.
              </p>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setImageEditDialog(prev => ({ ...prev, open: false }));
                setMediaLibraryOpen(true);
              }}
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Replace from Media Library
            </Button>
          </div>
          <DialogFooter className="flex-row gap-2 sm:gap-0">
            <Button
              variant="destructive"
              onClick={handleDeleteImage}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button onClick={handleUpdateImage}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Edit Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => !open && setLinkDialog({ open: false, url: '', openInNewTab: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4" />
              Edit Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={linkDialog.url}
                onChange={(e) => setLinkDialog(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveLink();
                  }
                }}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="openInNewTab"
                checked={linkDialog.openInNewTab}
                onCheckedChange={(checked) => setLinkDialog(prev => ({ ...prev, openInNewTab: checked === true }))}
              />
              <Label htmlFor="openInNewTab" className="text-sm font-normal cursor-pointer">
                Open in new tab
              </Label>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 sm:gap-0">
            {editor?.isActive('link') && (
              <Button
                variant="destructive"
                onClick={handleRemoveLink}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove Link
              </Button>
            )}
            <Button onClick={handleSaveLink}>
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML Embed Dialog */}
      <Dialog open={htmlDialog.open} onOpenChange={(open) => !open && setHtmlDialog({ open: false, html: '' })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-4 w-4" />
              Insert HTML / Embed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>HTML Code</Label>
              <textarea
                value={htmlDialog.html}
                onChange={(e) => setHtmlDialog(prev => ({ ...prev, html: e.target.value }))}
                placeholder="Paste your iframe or HTML embed code here..."
                className="w-full h-40 p-3 text-sm font-mono border rounded-md bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Paste embed codes from YouTube, Vimeo, Twitter, or any other iframe-based embed.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHtmlDialog({ open: false, html: '' })}>
              Cancel
            </Button>
            <Button onClick={handleInsertHtml} disabled={!htmlDialog.html.trim()}>
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dynamicTypographyCSS && <style>{dynamicTypographyCSS}</style>}
      
      <style>{`
        .medium-editor {
          position: relative;
        }
        .medium-editor .ProseMirror {
          min-height: 400px;
        }
        .medium-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: hsl(var(--muted-foreground));
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .medium-editor .ProseMirror:focus {
          outline: none;
        }
        .medium-editor .ProseMirror blockquote {
          border-left: 3px solid hsl(var(--primary));
          padding-left: 1.5rem;
          font-style: italic;
          color: hsl(var(--muted-foreground));
          margin: 1.5rem 0;
        }
        .medium-editor .ProseMirror code {
          background: hsl(var(--muted));
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.9em;
        }
        .medium-editor .ProseMirror pre {
          background: hsl(var(--muted));
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        .medium-editor .ProseMirror pre code {
          background: none;
          padding: 0;
        }
        .medium-editor .ProseMirror ul,
        .medium-editor .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .medium-editor .ProseMirror li {
          margin: 0.5rem 0;
        }
        .medium-editor .ProseMirror hr {
          border: none;
          border-top: 1px solid hsl(var(--border));
          margin: 2rem 0;
        }
        .medium-editor .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .medium-editor .ProseMirror img:hover {
          opacity: 0.9;
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.3);
        }
        .medium-editor .ProseMirror img.ProseMirror-selectednode {
          outline: 2px solid hsl(var(--primary));
          outline-offset: 2px;
        }
        .medium-editor .ProseMirror .iframe-wrapper {
          margin: 1.5rem 0;
        }
        .medium-editor .ProseMirror .iframe-wrapper iframe {
          max-width: 100%;
          display: block;
        }
        /* Ensure floating menu works inside Drawer */
        [data-tippy-root] {
          z-index: 1300 !important;
        }
        .tippy-box {
          z-index: 1300 !important;
        }
      `}</style>
    </div>
  );
};

export default MediumStyleEditor;
